import { useSyncExternalStore } from 'react'

export type EndType = 'date' | 'hires' | 'budget'

export interface PlannerInputs {
  startDate: string | null
  endType: EndType
  endValue: number | null // hires or budget amount; for date we normalize to days
  dailySpend: number // target spend per day
  campaignType?: 'goal' | 'perpetual' // Type of campaign: goal-based or perpetual
  strategy?: 'static' | 'pulsed' | 'adaptive'
  autoRenew?: boolean
  pulseParams?: {
    periodDays: number
    onDays: number
    dailySpendOn: number
    phaseOffsetDays: number
    rampDays?: number
  } | null
  adaptiveParams?: {
    targetLower: number // fraction 0..1
    targetUpper: number // fraction 0..1
    lookaheadDays: number
    minOnDays: number
    minOffDays: number
    maxSpendPerDay: number
    maxDailySpendChange: number
    reoptimizeEveryDays?: number
  } | null
}

export interface SourceSnapshot {
  id: string
  name: string
  active: boolean
  spend_model: string
  color?: string | null
  cpa_bid?: number | null
  cpc?: number | null
  cpm?: number | null
  daily_budget?: number | null
  referral_bonus_per_hire?: number | null
  apps_override?: number | null
  diminishing_exponent?: number | null
  saturation_rate?: number | null
  // Optional funnel metrics (percent values 0..100 from Sources tab)
  funnel_app_to_interview?: number | null
  funnel_interview_to_offer?: number | null
  funnel_offer_to_hire?: number | null
}

interface PlanState {
  planner: PlannerInputs
  conversionRate: number // applicants -> hires (0..1)
  sources: SourceSnapshot[]
  liveView: boolean
}

function zonesFromLocalStorage(): { lowYellow: number; highYellow: number } | null {
  try {
    const raw = localStorage.getItem('passcom-plan-zones')
    if (!raw) return null
    const z = JSON.parse(raw)
    if (z && typeof z === 'object' && Number.isFinite(Number(z.lowYellow)) && Number.isFinite(Number(z.highYellow))) {
      return { lowYellow: Number(z.lowYellow), highYellow: Number(z.highYellow) }
    }
  } catch {}
  return null
}

// Initialize sources synchronously on state creation (before any component renders)
function loadInitialSources(): SourceSnapshot[] {
  try {
    // Try loading from localStorage first
    const raw = localStorage.getItem('passcom-sources-v1');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        // Map to SourceSnapshot format
        return parsed.map((s: any) => ({
          id: s.id,
          name: s.name,
          active: s.active !== false, // Default to true if undefined
          spend_model: s.spend_model,
          color: s.color ?? null,
          cpa_bid: s.cpa_bid ?? null,
          cpc: s.cpc ?? null,
          cpm: s.cpm ?? null,
          daily_budget: s.daily_budget ?? null,
          referral_bonus_per_hire: s.referral_bonus_per_hire ?? null,
          apps_override: s.apps_override ?? null,
          diminishing_exponent: s.diminishing_exponent ?? null,
          saturation_rate: s.saturation_rate ?? null,
          funnel_app_to_interview: s.funnel_app_to_interview ?? null,
          funnel_interview_to_offer: s.funnel_interview_to_offer ?? null,
          funnel_offer_to_hire: s.funnel_offer_to_hire ?? null,
        }));
      }
    }
  } catch (e) {
    console.warn('Failed to load sources from localStorage:', e);
  }

  // No saved sources - return default seed sources
  return [
    {
      id: "src_indeed_001",
      name: "Indeed Sponsored",
      active: true,
      spend_model: "cpc",
      color: "#2563eb",
      cpa_bid: null,
      cpc: 1.8,
      cpm: null,
      daily_budget: 350,
      referral_bonus_per_hire: null,
      apps_override: null,
      diminishing_exponent: 0.85,
      saturation_rate: null,
      funnel_app_to_interview: 5,
      funnel_interview_to_offer: 40,
      funnel_offer_to_hire: 81,
    },
    {
      id: "src_fb_001",
      name: "Facebook Ads",
      active: true,
      spend_model: "cpm",
      color: "#4f46e5",
      cpa_bid: null,
      cpc: null,
      cpm: 9,
      daily_budget: 200,
      referral_bonus_per_hire: null,
      apps_override: null,
      diminishing_exponent: 0.85,
      saturation_rate: null,
      funnel_app_to_interview: 5,
      funnel_interview_to_offer: 40,
      funnel_offer_to_hire: 81,
    },
    {
      id: "src_ref_001",
      name: "Employee Referrals",
      active: true,
      spend_model: "referral",
      color: "#10b981",
      cpa_bid: null,
      cpc: null,
      cpm: null,
      daily_budget: null,
      referral_bonus_per_hire: 300,
      apps_override: 5,
      diminishing_exponent: null,
      saturation_rate: 0.18,
      funnel_app_to_interview: 5,
      funnel_interview_to_offer: 40,
      funnel_offer_to_hire: 81,
    },
    {
      id: "src_org_001",
      name: "Word of Mouth",
      active: false,
      spend_model: "organic",
      color: "#334155",
      cpa_bid: null,
      cpc: null,
      cpm: null,
      daily_budget: null,
      referral_bonus_per_hire: null,
      apps_override: 3,
      diminishing_exponent: null,
      saturation_rate: null,
      funnel_app_to_interview: 5,
      funnel_interview_to_offer: 40,
      funnel_offer_to_hire: 81,
    },
  ];
}

let state: PlanState = {
  planner: {
    startDate: null,
    endType: 'budget',
    endValue: null,
    dailySpend: 0,
    strategy: 'static',
    autoRenew: false,
    pulseParams: { periodDays: 14, onDays: 5, dailySpendOn: 100, phaseOffsetDays: 0, rampDays: 0 },
    adaptiveParams: (() => {
      const z = zonesFromLocalStorage()
      const lower = z ? Math.max(0, Math.min(2, z.lowYellow / 100)) : 0.95
      const upper = z ? Math.max(lower, Math.min(3, z.highYellow / 100)) : 1.05
      return { targetLower: lower, targetUpper: upper, lookaheadDays: 3, minOnDays: 3, minOffDays: 3, maxSpendPerDay: 1000, maxDailySpendChange: 100, reoptimizeEveryDays: 7 }
    })()
  },
  conversionRate: 0.2,
  sources: loadInitialSources(), // ✅ Load sources synchronously on state init!
  liveView: true,
}
// hydrate from localStorage
try {
  const savedPlanner = localStorage.getItem('passcom-planner-v1')
  if (savedPlanner) {
    const pl = JSON.parse(savedPlanner)
    if (pl && typeof pl === 'object') {
      state.planner = { ...state.planner, ...pl }
    }
  }
  // If no adaptive params persisted, seed from current zones control
  if (!state.planner.adaptiveParams) {
    const z = zonesFromLocalStorage()
    if (z) {
      state.planner.adaptiveParams = {
        targetLower: Math.max(0, Math.min(2, z.lowYellow / 100)),
        targetUpper: Math.max(0, Math.min(3, z.highYellow / 100)),
        lookaheadDays: 3,
        minOnDays: 3,
        minOffDays: 3,
        maxSpendPerDay: 1000,
        maxDailySpendChange: 100,
        reoptimizeEveryDays: 7,
      }
    }
  }
  const savedLive = localStorage.getItem('passcom-liveview')
  if (savedLive != null) {
    state.liveView = savedLive === 'true'
  }
} catch {}

let version = 0
const listeners = new Set<() => void>()
const emit = () => { version++; listeners.forEach(l => l()) }

export function setPlanner(patch: Partial<PlannerInputs>, persist: boolean = true) {
  state = { ...state, planner: { ...state.planner, ...patch } }
  if (persist) {
    try { localStorage.setItem('passcom-planner-v1', JSON.stringify(state.planner)) } catch {}
  }
  emit()
}

export function persistPlanner() {
  try { localStorage.setItem('passcom-planner-v1', JSON.stringify(state.planner)) } catch {}
}

export function setConversionRate(rate: number) {
  const r = Math.max(0, Math.min(1, Number(rate) || 0))
  state = { ...state, conversionRate: r }
  emit()
}

export function setSourcesSnapshot(sources: SourceSnapshot[]) {
  state = { ...state, sources: sources.map(s => ({ ...s })) }
  emit()
}

export function setLiveView(enabled: boolean) {
  state = { ...state, liveView: !!enabled }
  try { localStorage.setItem('passcom-liveview', String(state.liveView)) } catch {}
  emit()
}

export function useCampaignPlanVersion(): number {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb) },
    () => version,
    () => version,
  )
}

// Compute effective daily spend for a specific date based on strategy parameters.
export function getEffectiveDailySpend(date: Date = new Date()): number {
  const p = state.planner
  const base = Math.max(0, Number(p.dailySpend || 0))
  const strategy = p.strategy || 'static'
  if (strategy === 'pulsed') {
    const params = p.pulseParams || { periodDays: 14, onDays: 5, dailySpendOn: base || 0, phaseOffsetDays: 0 }
    const startISO = p.startDate
    if (!startISO) return params.dailySpendOn || base
    const start = new Date(startISO)
    start.setHours(0,0,0,0)
    const d0 = new Date(date)
    d0.setHours(0,0,0,0)
    const daysSinceStart = Math.max(0, Math.floor((d0.getTime() - start.getTime()) / (1000*60*60*24)))
    const phase = (daysSinceStart - (params.phaseOffsetDays || 0)) % Math.max(1, params.periodDays || 1)
    const on = phase >= 0 && phase < Math.max(0, params.onDays || 0)
    return on ? Math.max(0, Number(params.dailySpendOn || base)) : 0
  }
  // adaptive currently uses base spend; controller can adjust daily via UI or scheduled job
  return base
}

const APPLY_CPC = 0.12
const APPLY_DAILY = 0.10
const CTR = 0.015
const DEFAULT_FUNNEL_CONV = 0.64 * 0.84 * 0.86 * 0.60
const DEFAULT_DIMINISHING_EXPONENT = 0.85
const DEFAULT_SATURATION_RATE = 0.18
const clampValue = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))
const getBeta = (s: SourceSnapshot) => {
  const candidate = Number((s as any).diminishing_exponent)
  if (!Number.isFinite(candidate)) return DEFAULT_DIMINISHING_EXPONENT
  return clampValue(candidate, 0.3, 1)
}
const getSaturationRate = (s: SourceSnapshot) => {
  const candidate = Number((s as any).saturation_rate)
  if (!Number.isFinite(candidate)) return DEFAULT_SATURATION_RATE
  return clampValue(candidate, 0.01, 1)
}
const getReferenceSpend = (s: SourceSnapshot) => {
  const candidate = Number(s.daily_budget)
  if (Number.isFinite(candidate) && candidate > 0) return Math.max(1, candidate)
  return 100
}
const linearAppsAtSpend = (s: SourceSnapshot, spend: number, conv: number): number => {
  if (spend <= 0) return 0
  switch (s.spend_model) {
    case 'cpa':
      return spend / Math.max(0.0001, Number(s.cpa_bid || 10))
    case 'daily_budget': {
      // For daily_budget, use explicit CPA bid when present; otherwise derive CPA from CPC using APPLY_DAILY
      const effectiveBid = (Number((s as any).cpa_bid) && Number((s as any).cpa_bid) > 0)
        ? Math.max(0.0001, Number((s as any).cpa_bid))
        : Math.max(0.0001, Number((s as any).cpc || 2)) / APPLY_DAILY
      const bid = effectiveBid
      return spend / bid
    }
    case 'cpc': {
      const cpc = Math.max(0.0001, Number(s.cpc || 2))
      const clicks = spend / cpc
      return clicks * APPLY_CPC
    }
    case 'cpm': {
      const cpm = Math.max(0.0001, Number(s.cpm || 10))
      const impressions = (spend / cpm) * 1000
      const clicks = impressions * CTR
      return clicks * APPLY_DAILY
    }
    case 'referral': {
      const bounty = Math.max(0.0001, Number(s.referral_bonus_per_hire || 0))
      const maxApps = Math.max(0, Number(s.apps_override || 0))
      if (bounty <= 0 || conv <= 0) return 0
      const linear = spend / (bounty * conv)
      return Math.min(maxApps, linear)
    }
    default:
      return 0
  }
}
const spendToApps = (s: SourceSnapshot, spend: number, conv: number): number => {
  spend = Math.max(0, spend)
  if (spend <= 0) return 0
  if (s.spend_model === 'referral') {
    const maxApps = Math.max(0, Number(s.apps_override || 0))
    if (maxApps <= 0) return 0
    const bounty = Math.max(0.0001, Number(s.referral_bonus_per_hire || 0))
    if (bounty <= 0 || conv <= 0) return 0
    const scaled = spend / (bounty * conv)
    const k = getSaturationRate(s)
    return maxApps * (1 - Math.exp(-k * scaled))
  }
  const beta = getBeta(s)
  const Sref = getReferenceSpend(s)
  const Lref = linearAppsAtSpend(s, Sref, conv)
  if (Lref <= 0) return 0
  const r = Lref / Math.pow(Sref, beta)
  return r * Math.pow(spend, beta)
}
const marginalAppsPerDollar = (s: SourceSnapshot, spend: number, conv: number): number => {
  const delta = 1
  const a1 = spendToApps(s, spend, conv)
  const a2 = spendToApps(s, spend + delta, conv)
  return Math.max(0, a2 - a1) / delta
}

// helpers
function daysBetween(startISO: string | null, date: Date): number {
  if (!startISO) return Infinity
  const s = new Date(startISO)
  s.setHours(0,0,0,0)
  const d0 = new Date(date)
  d0.setHours(0,0,0,0)
  const diff = (d0.getTime() - s.getTime()) / (1000*60*60*24)
  return Math.floor(diff)
}

export function isActiveOn(date: Date): boolean {
  if (!state.liveView) return false
  const { planner } = state
  if (!planner.startDate) return false
  if (daysBetween(planner.startDate, date) < 0) return false
  // Auto-renew means the campaign remains active indefinitely after start
  if (planner.autoRenew) {
    const dailySpend = Math.max(0, planner.dailySpend)
    return dailySpend > 0
  }

  switch (planner.endType) {
    case 'date': {
      if (!planner.endValue) return true
      // endValue as days
      return daysBetween(planner.startDate, date) <= planner.endValue
    }
    case 'hires': {
      if (!planner.endValue) return true
      const hpd = getHiresPerDay()
      if (hpd <= 0) return false
      const activeDays = Math.ceil(planner.endValue / hpd)
      return daysBetween(planner.startDate, date) < activeDays
    }
    case 'budget': {
      if (!planner.endValue) return true
      const dailySpend = Math.max(0, planner.dailySpend)
      if (dailySpend <= 0) return false
      const activeDays = Math.ceil(planner.endValue / dailySpend)
      return daysBetween(planner.startDate, date) < activeDays
    }
  }
}

export function isScheduledOn(date: Date): boolean {
  const { planner } = state
  if (!planner.startDate) return false
  if (daysBetween(planner.startDate, date) < 0) return false
  // Auto-renew: scheduled forever after start (subject to daily spend > 0)
  if (planner.autoRenew) {
    const dailySpend = Math.max(0, planner.dailySpend)
    return dailySpend > 0
  }
  switch (planner.endType) {
    case 'date': {
      if (!planner.endValue) return true
      return daysBetween(planner.startDate, date) <= planner.endValue
    }
    case 'hires': {
      if (!planner.endValue) return true
      const hpd = getHiresPerDay()
      if (hpd <= 0) return false
      // Extend the scheduled window by onboarding lag to allow hires to materialize
      const ONBOARDING_DELAY_DAYS = 3
      const activeDays = Math.ceil(planner.endValue / hpd)
      return daysBetween(planner.startDate, date) < (activeDays + ONBOARDING_DELAY_DAYS)
    }
    case 'budget': {
      if (!planner.endValue) return true
      const dailySpend = Math.max(0, planner.dailySpend)
      if (dailySpend <= 0) return false
      const activeDays = Math.ceil(planner.endValue / dailySpend)
      return daysBetween(planner.startDate, date) < activeDays
    }
  }
}

// Derive blended apps/day from spend and sources
// Unified allocator readout to keep Plan/Review/Campaign tabs consistent
export function getAppsAndHiresPerDay(): {
  apps: number;
  hires: number;
  alloc: Map<string, number>;
  perSourceApps: Map<string, number>;
} {
  const { planner, sources } = state
  const dailyLimit = Math.max(0, Number(planner.dailySpend || 0))
  const liveSources = sources.filter(s => s.active)
  if (liveSources.length === 0) return { apps: 0, hires: 0, alloc: new Map(), perSourceApps: new Map() }
  const overallConv = Math.max(0, Math.min(1, Number(state.conversionRate) || 0))

  const funnelConvToHire = (s: SourceSnapshot): number => {
    const r1 = clampValue(Number((s as any).funnel_app_to_interview ?? 5) / 100, 0, 1)
    const r2 = clampValue(Number((s as any).funnel_interview_to_offer ?? 40) / 100, 0, 1)
    const r3 = clampValue(Number((s as any).funnel_offer_to_hire ?? 81) / 100, 0, 1)
    return r1 * r2 * r3
  }
  const fallbackConv = overallConv > 0 ? overallConv : DEFAULT_FUNNEL_CONV
  const perSourceConv = (s: SourceSnapshot) => {
    const conv = funnelConvToHire(s)
    return conv > 0 ? conv : Math.max(0.0001, fallbackConv)
  }
  const effectiveCPH = (s: SourceSnapshot): number => {
    const conv = Math.max(0.0001, perSourceConv(s))
    switch (s.spend_model) {
      case 'referral':
        return Math.max(0.0001, Number((s as any).referral_bonus_per_hire || 0))
      case 'cpa': {
        const cpa = Math.max(0.0001, Number(s.cpa_bid || 10))
        return cpa / conv
      }
      case 'cpc': {
        const cpc = Math.max(0.0001, Number(s.cpc || 2))
        const cpa = cpc / APPLY_CPC
        return cpa / conv
      }
      case 'cpm': {
        const cpm = Math.max(0.0001, Number(s.cpm || 10))
        const cpa = cpm / (1000 * CTR * APPLY_DAILY)
        return cpa / conv
      }
      case 'daily_budget': {
      // Use CPA bid when provided; else derive from CPC using APPLY_DAILY
      const cpa = (Number((s as any).cpa_bid) && Number((s as any).cpa_bid) > 0)
        ? Math.max(0.0001, Number((s as any).cpa_bid))
        : Math.max(0.0001, Number((s as any).cpc || 2)) / APPLY_DAILY
        return cpa / conv
      }
      default:
        return Number.POSITIVE_INFINITY
    }
  }

  let remaining = dailyLimit
  const alloc = new Map<string, number>()

  // 1) Threshold daily_budget sources: allocate only if fully funded
  const threshold = liveSources
    .filter((s) => {
      if (s.spend_model !== 'daily_budget') return false
      const budget = Number(s.daily_budget || 0)
      if (budget <= 0) return false
      const hasCpaBid = Number((s as any).cpa_bid || 0) > 0
      const hasCpc = Number((s as any).cpc || 0) > 0
      return hasCpaBid || hasCpc
    })
    .sort((a, b) => effectiveCPH(a) - effectiveCPH(b))
  for (const s of threshold) {
    const need = Math.max(0, Number(s.daily_budget || 0))
    if (remaining >= need) {
      alloc.set(s.id, need)
      remaining -= need
    } else {
      alloc.set(s.id, 0)
    }
  }

  // 2) Scalable sources with diminishing returns
  type Scalable = { s: SourceSnapshot; conv: number; cap: number; spent: number }
  const scalable: Scalable[] = liveSources
    .filter((s) => s.spend_model === 'referral' || s.spend_model === 'cpc' || s.spend_model === 'cpm' || s.spend_model === 'cpa')
    .map((s) => {
      const conv = perSourceConv(s)
      const cap =
        s.spend_model === 'referral'
          ? Math.max(0, Number(s.referral_bonus_per_hire || 0)) * Math.max(0, Number(s.apps_override || 0)) * conv
          : (Number.isFinite(Number(s.daily_budget)) ? Math.max(0, Number(s.daily_budget)) : Number.POSITIVE_INFINITY)
      return { s, conv, cap, spent: 0 }
    })

  const step = 1
  let guard = 0
  while (remaining > 0 && guard < 5000) {
    guard++
    let bestIdx = -1
    let bestScore = 0
    for (let i = 0; i < scalable.length; i++) {
      const it = scalable[i]
      if (it.spent >= it.cap) continue
      const margApps = marginalAppsPerDollar(it.s, it.spent, it.conv)
      const score = margApps * it.conv
      if (score > bestScore) {
        bestScore = score
        bestIdx = i
      }
    }
    if (bestIdx < 0 || bestScore <= 0) break
    const it = scalable[bestIdx]
    const room = it.cap - it.spent
    const take = Math.min(remaining, Math.max(1, Math.min(step, room)))
    it.spent += take
    remaining -= take
  }
  scalable.forEach((it) => {
    if (it.spent > 0) {
      alloc.set(it.s.id, (alloc.get(it.s.id) || 0) + it.spent)
    }
  })
  // Last-mile fill for threshold sources if budget remains
  if (remaining > 0) {
    const threshold = liveSources
      .filter((s) => {
        if (s.spend_model !== 'daily_budget') return false
        const budget = Number(s.daily_budget || 0)
        if (budget <= 0) return false
        const hasCpaBid = Number((s as any).cpa_bid || 0) > 0
        const hasCpc = Number((s as any).cpc || 0) > 0
        return hasCpaBid || hasCpc
      })
    .sort((a, b) => effectiveCPH(a) - effectiveCPH(b))
    for (const s of threshold) {
      const need = Math.max(0, Number(s.daily_budget || 0))
      const already = alloc.get(s.id) || 0
      if (already < need && remaining >= need) {
        alloc.set(s.id, need)
        remaining -= need
      }
    if (remaining <= 0) break
    }
  }

  // 3) Compute applicants/day from allocations (exclude organic)
  let applicants = 0
  const perSourceApps = new Map<string, number>()
  for (const s of liveSources) {
    if (s.spend_model === 'organic') continue
    const conv = perSourceConv(s)
    const spent = alloc.get(s.id) || 0
    if (s.spend_model === 'daily_budget') {
      const need = Math.max(0, Number(s.daily_budget || 0))
      if (spent >= need) {
        const apps = spendToApps(s, spent, conv)
        applicants += apps
        perSourceApps.set(s.id, apps)
      }
    } else if (spent > 0) {
      const apps = spendToApps(s, spent, conv)
      applicants += apps
      perSourceApps.set(s.id, apps)
    }
  }

  // Compute continuous hires/day using per-source funnel
  let hires = 0
  for (const s of liveSources) {
    const apps = perSourceApps.get(s.id) || 0
    if (apps <= 0) continue
    const r1 = clampValue(Number((s as any).funnel_app_to_interview ?? 5) / 100, 0, 1)
    const r2 = clampValue(Number((s as any).funnel_interview_to_offer ?? 40) / 100, 0, 1)
    const r3 = clampValue(Number((s as any).funnel_offer_to_hire ?? 81) / 100, 0, 1)
    hires += apps * r1 * r2 * r3
  }

  return { apps: applicants, hires, alloc, perSourceApps }
  }

export function getApplicantsPerDay(): number {
  return getAppsAndHiresPerDay().apps
}

export function getHiresPerDay(): number {
  return getAppsAndHiresPerDay().hires
}

export function getExtraSupplyHalfHoursPerDay(): number {
  // Each hire contributes 30 hours/week = 60 half-hours per week = ~8.571 per day
  const hires = getHiresPerDay()
  return (hires * 30 / 7) * 2
}

export function getStateSnapshot() { return state }

export function getDerivedFromCriterion(opts: {
  startISO: string | null;
  endType: EndType;
  endValue: number | null; // days for 'date', hires for 'hires', dollars for 'budget'
  dailySpend: number;
}): { days: number; budget: number; hires: number; endDate: string | null } {
  const startISO = opts.startISO;
  const S = Math.max(0, Number(opts.dailySpend||0));
  const A = getApplicantsPerDay();
  const r = getStateSnapshot().conversionRate;
  const Hpd = A * r;
  const toISO = (d: Date): string | null => {
    const t = d instanceof Date ? d.getTime() : NaN;
    if (isNaN(t)) return null;
    try {
      return new Date(t).toISOString().slice(0, 10);
    } catch {
      return null;
    }
  };
  const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };

  if (!startISO) return { days: 0, budget: 0, hires: 0, endDate: null };
  const start = new Date(startISO);
  // Guard invalid date strings to prevent RangeError in toISOString
  if (isNaN(start.getTime())) {
    return { days: 0, budget: 0, hires: 0, endDate: null };
  }

  const clampPos = (n: number) => Math.max(0, Number.isFinite(n) ? n : 0);

  let days = 0, budget = 0, hires = 0;
  if (opts.endType === 'date') {
    days = clampPos(Number(opts.endValue||0));
    budget = S * days;
    hires = Hpd * days;
  } else if (opts.endType === 'hires') {
    const H = clampPos(Number(opts.endValue||0));
    // No capacity to hire: short-circuit, avoid runaway days/endDate
    if (Hpd <= 0) {
      return { days: 0, budget: 0, hires: H, endDate: null };
    }
    days = clampPos(H / Math.max(Hpd, 1e-6));
    budget = S * days;
    hires = H;
  } else { // 'budget'
    const B = clampPos(Number(opts.endValue||0));
    // No budget/day capacity: short-circuit
    if (S <= 0) {
      return { days: 0, budget: B, hires: 0, endDate: null };
    }
    days = clampPos(B / Math.max(S, 1e-6));
    budget = B;
    hires = Hpd * days;
  }

  const endDate = days > 0 ? toISO(addDays(start, Math.round(days))) : null;
  return { days, budget, hires, endDate };
}

// ---- Shared cap calculator for Daily Budget sliders across tabs ----
export function getMaxDailySpendCap(): number {
  const stateSnap = getStateSnapshot();
  const sources = stateSnap.sources || [];
  // Use a consistent conversion for referral caps (align with Review panel defaults)
  const DEFAULT_CONV_FOR_CAP = 0.64 * 0.84 * 0.86 * 0.60; // ~0.277
  let cap = 0;
  for (const s of sources) {
    if (!s || !s.active) continue;
    if (s.spend_model === 'organic') continue;
    if (s.spend_model === 'referral') {
      const bounty = Math.max(0, Number(s.referral_bonus_per_hire || 0));
      const apps = Math.max(0, Number(s.apps_override || 0));
      cap += bounty * apps * Math.max(0.0001, DEFAULT_CONV_FOR_CAP);
    } else if (s.spend_model === 'daily_budget') {
      cap += Math.max(0, Number(s.daily_budget || 0));
    } else {
      // scalable; if a per-source daily_budget exists, treat as cap; otherwise do not increase max
      if (Number.isFinite(Number(s.daily_budget)) && Number(s.daily_budget) > 0) {
        cap += Math.max(0, Number(s.daily_budget));
      }
    }
  }
  const raw = Math.round(cap);
  return Math.max(0, raw);
}

// ---- Developer sanity probe for allocator monotonicity (manual) ----
declare global {
  interface Window {
    passcomRunAllocatorSanity?: () => void
  }
}
function attachAllocatorSanityProbe() {
  if (typeof window === 'undefined') return
  if (window.passcomRunAllocatorSanity) return
  window.passcomRunAllocatorSanity = () => {
    try {
      const original = getStateSnapshot().planner.dailySpend
      const spends = [50, 100, 200, 400, 800]
      const rows: Array<{ spend: number; apps: number; hires: number }> = []
      for (const s of spends) {
        setPlanner({ dailySpend: s })
        const apps = getApplicantsPerDay()
        const hires = getHiresPerDay()
        rows.push({ spend: s, apps, hires })
      }
      setPlanner({ dailySpend: original })
      // eslint-disable-next-line no-console
      console.table(rows)
      // eslint-disable-next-line no-console
      console.info('Allocator sanity: expect apps/hires to be non-decreasing with spend (sublinear at higher spends).')
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Sanity probe failed:', err)
    }
  }
}
attachAllocatorSanityProbe()

